
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { Vendor, Owner, InvestmentEntity, InvestmentEntityInvestor } from '@repo/shared-types';
import { useToast } from '../../components/common/ToastContext';
import { vendorSchema } from '@repo/shared-types/core/vendorSchemas';
import { ownerSchema } from '@repo/shared-types/property/schemas';
import { entityNoteListItemSchema } from '@repo/shared-types/common/noteSchemas';
import type { Note } from '@repo/shared-types/common/notes';
import { investorSchema, investmentEntitySchema, entityInvestorSchema } from '@repo/shared-types/investments/schemas';
import { z } from 'zod';
import { validateEntity, validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

// --- VENDORS ---
export const useVendors = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['vendors', filters ?? {}],
    queryFn: async () => {
      const raw = await api.maintenance.vendors.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Vendor[] }).data;
      const validation = validateEntityArray(vendorSchema, list);
      if (!validation.valid) {
        logger.error('Invalid vendors query response:', validation.errors);
        throw new Error('Invalid vendors data received from API');
      }
      return validation.data! as Vendor[];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useVendorsPaginated = (filters: Record<string, string>) => {
  return useQuery({
    queryKey: ['vendors', 'paginated', filters],
    queryFn: async () => {
      const raw = await api.maintenance.vendors.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Vendor[] }).data;
      const total = Array.isArray(raw) ? raw.length : (raw as { data: Vendor[]; total: number }).total;
      const validation = validateEntityArray(vendorSchema, list);
      if (!validation.valid) {
        logger.error('Invalid vendors query response:', validation.errors);
        throw new Error('Invalid vendors data received from API');
      }
      return { data: validation.data! as Vendor[], total };
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateVendor = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (vendor: Vendor) => api.maintenance.vendors.create(vendor),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema (if data is returned)
      if (data) {
        const validation = validateEntity(vendorSchema, data);
        if (!validation.valid) {
          logger.error('Invalid vendor response from API:', validation.errors);
          showToast('Vendor created but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['vendors'] });
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      showToast('Vendor added successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to add vendor', 'error');
    }
  });
};

export const useUpdateVendor = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Vendor> }) => api.maintenance.vendors.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(vendorSchema, data);
      if (!validation.valid) {
        logger.error('Invalid vendor response from API:', validation.errors);
        showToast('Vendor updated but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['vendors'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      showToast('Vendor updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update vendor', 'error');
    }
  });
};

export const useDeleteVendor = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.maintenance.vendors.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      showToast('Vendor deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete vendor', 'error');
    }
  });
};

// --- OWNERS ---
export const useOwners = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['owners', filters ?? {}],
    queryFn: async () => {
      const raw = await api.owners.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Owner[] }).data;
      const validation = validateEntityArray(ownerSchema, list);
      if (!validation.valid) {
        logger.error('Invalid owners query response:', validation.errors);
        throw new Error('Invalid owners data received from API');
      }
      return validation.data! as Owner[];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useOwnersPaginated = (filters: Record<string, string>) => {
  return useQuery({
    queryKey: ['owners', 'paginated', filters],
    queryFn: async () => {
      const raw = await api.owners.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Owner[] }).data;
      const total = Array.isArray(raw) ? raw.length : (raw as { data: Owner[]; total: number }).total;
      const validation = validateEntityArray(ownerSchema, list);
      if (!validation.valid) {
        logger.error('Invalid owners query response:', validation.errors);
        throw new Error('Invalid owners data received from API');
      }
      return { data: validation.data! as Owner[], total };
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateOwner = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (owner: Owner) => api.owners.create(owner),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema (if data is returned)
      if (data) {
        const validation = validateEntity(ownerSchema, data);
        if (!validation.valid) {
          logger.error('Invalid owner response from API:', validation.errors);
          showToast('Owner created but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['owners'] });
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      showToast('Owner created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create owner', 'error');
    }
  });
};

export const useUpdateOwner = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Owner> }) => api.owners.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema (if data is returned)
      if (data) {
        const validation = validateEntity(ownerSchema, data);
        if (!validation.valid) {
          logger.error('Invalid owner response from API:', validation.errors);
          showToast('Owner updated but received invalid data from server', 'error');
          queryClient.invalidateQueries({ queryKey: ['owners'] });
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      showToast('Owner updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update owner', 'error');
    }
  });
};

export const useDeleteOwner = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.owners.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      showToast('Owner deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete owner', 'error');
    }
  });
};

// --- INVESTORS ---
export const useInvestors = () => {
  return useQuery({
    queryKey: ['investors'],
    queryFn: async () => {
      const data = await api.investors.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(investorSchema as z.ZodType<import('types').Investor>, data);
      if (!validation.valid) {
        logger.warn('Investors query response did not match schema; returning empty list', { errors: validation.errors });
        return [] as import('types').Investor[];
      }
      return validation.data!;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// --- INVESTMENT ENTITIES ---
export const useInvestmentEntities = () => {
  return useQuery({
    queryKey: ['investmentEntities'],
    queryFn: async () => {
      const data = await api.investmentEntities.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(investmentEntitySchema, data);
      if (!validation.valid) {
        logger.error('Invalid investment entities query response:', validation.errors);
        throw new Error('Invalid investment entities data received from API');
      }
      return validation.data! as InvestmentEntity[];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useInvestmentEntity = (id: string) => {
  return useQuery({
    queryKey: ['investmentEntities', id],
    queryFn: async () => {
      const data = await api.investmentEntities.get(id);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(investmentEntitySchema, data);
        if (!validation.valid) {
          logger.error('Invalid investment entity query response:', validation.errors);
          throw new Error('Invalid investment entity data received from API');
        }
        return validation.data! as InvestmentEntity;
      }
      return null;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateInvestmentEntity = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (entity: InvestmentEntity) => api.investmentEntities.create(entity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investmentEntities'] });
      showToast('Investment entity created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create investment entity', 'error');
    }
  });
};

export const useUpdateInvestmentEntity = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<InvestmentEntity> }) =>
      api.investmentEntities.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investmentEntities'] });
      showToast('Investment entity updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update investment entity', 'error');
    }
  });
};

export const useDeleteInvestmentEntity = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.investmentEntities.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investmentEntities'] });
      showToast('Investment entity deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete investment entity', 'error');
    }
  });
};

// --- ENTITY-INVESTOR RELATIONSHIPS ---
export const useEntityInvestors = (entityId?: string, investorId?: string) => {
  return useQuery({
    queryKey: ['entityInvestors', entityId, investorId],
    queryFn: async () => {
      const data = await api.entityInvestors.list(entityId, investorId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(entityInvestorSchema, data);
      if (!validation.valid) {
        logger.error('Invalid entity investors query response:', validation.errors);
        throw new Error('Invalid entity investors data received from API');
      }
      return validation.data! as InvestmentEntityInvestor[];
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });
};

export const useEntityInvestor = (id: string) => {
  return useQuery({
    queryKey: ['entityInvestors', id],
    queryFn: async () => {
      const data = await api.entityInvestors.get(id);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(entityInvestorSchema, data);
        if (!validation.valid) {
          logger.error('Invalid entity investor query response:', validation.errors);
          throw new Error('Invalid entity investor data received from API');
        }
        return validation.data! as InvestmentEntityInvestor;
      }
      return null;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateEntityInvestor = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (relationship: InvestmentEntityInvestor) => api.entityInvestors.create(relationship),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(entityInvestorSchema, data);
      if (!validation.valid) {
        logger.error('Invalid entity investor response from API:', validation.errors);
        showToast('Entity-investor relationship created but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['entityInvestors'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['entityInvestors'] });
      queryClient.invalidateQueries({ queryKey: ['investors'] });
      showToast('Entity-investor relationship created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create entity-investor relationship', 'error');
    }
  });
};

export const useUpdateEntityInvestor = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<InvestmentEntityInvestor> }) =>
      api.entityInvestors.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(entityInvestorSchema, data);
      if (!validation.valid) {
        logger.error('Invalid entity investor response from API:', validation.errors);
        showToast('Entity-investor relationship updated but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['entityInvestors'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['entityInvestors'] });
      queryClient.invalidateQueries({ queryKey: ['investors'] });
      showToast('Entity-investor relationship updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update entity-investor relationship', 'error');
    }
  });
};

export const useDeleteEntityInvestor = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.entityInvestors.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entityInvestors'] });
      queryClient.invalidateQueries({ queryKey: ['investors'] });
      showToast('Entity-investor relationship deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete entity-investor relationship', 'error');
    }
  });
};

// --- OWNER NOTES ---
export const useOwnerNotes = (ownerId: string) => {
  return useQuery({
    queryKey: ['owners', 'notes', ownerId],
    queryFn: async () => {
      const data = await api.owners.notes.list(ownerId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(entityNoteListItemSchema, data);
      if (!validation.valid) {
        logger.error('Invalid owner notes query response:', validation.errors);
        throw new Error('Invalid owner notes data received from API');
      }
      return validation.data! as Note[];
    },
    enabled: !!ownerId,
  });
};

export const useCreateOwnerNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (note: { ownerId: string; content: string; operatingOrganizationId?: string }) =>
      api.owners.notes.create(note),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['owners', 'notes', variables.ownerId] });
      showToast('Note created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create note', 'error');
    }
  });
};

export const useUpdateOwnerNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { content: string } }) =>
      api.owners.notes.update(id, updates),
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['owners', 'notes'] });
      showToast('Note updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update note', 'error');
    }
  });
};

export const useDeleteOwnerNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.owners.notes.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners', 'notes'] });
      showToast('Note deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete note', 'error');
    }
  });
};

// --- TENANTS ---
// Re-export from useTenants.ts for convenience
export { useTenants } from './useTenants';


